import React, { useEffect, useMemo } from 'react';
import { RouterProvider } from 'react-router-dom';
import { UserProvider } from './contexts/UserContext';
import { SnackbarProvider } from 'notistack';
import ErrorBoundary from './components/ErrorBoundary';
import { createAppRouter } from './router';
import SessionExpiredMessage from './components/SessionExpiredMessage';
import GoogleAnalytics from './components/GoogleAnalytics';

const App = () => {
  // App sits ABOVE UserProvider in the tree — so it cannot call useUser().
  // The router and any pages that DO live under UserProvider can read auth
  // state via useUser() themselves.

  // Create router inside component to avoid HMR issues
  // This ensures router is recreated on hot reload, preventing stale context issues
  const router = useMemo(() => createAppRouter(), []);

  // Check if user is on the problematic logout URL
  useEffect(() => {
    const currentUrl = window.location.href;
    if (currentUrl.includes('id_token_hint') && currentUrl.includes('post_logout_redirect_uri')) {
      console.log('Detected problematic logout URL, redirecting to session expired message');

      // Clear any stale state
      try {
        localStorage.clear();
        sessionStorage.clear();

        // Clear any cookies from our domain
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf('=');
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          // Only clear cookies from our domain
          if (!name.includes('amazoncognito')) {
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          }
        }
      } catch (error) {
        console.warn('Error clearing storage:', error);
      }

      // Show session expired message instead of the 400 error
      document.body.innerHTML = '';
      const root = document.createElement('div');
      root.id = 'root';
      document.body.appendChild(root);

      // Use dynamic import to avoid issues with ReactDOM
      import('react-dom/client').then(({ createRoot }) => {
        createRoot(root).render(<SessionExpiredMessage />);
      }).catch(() => {
        // Fallback if dynamic import fails
        window.location.href = '/';
      });
    }
  }, []);

  return (
    <ErrorBoundary>
      <SnackbarProvider maxSnack={3}>
        <UserProvider>
          <GoogleAnalytics router={router} />
          <RouterProvider router={router} />
        </UserProvider>
      </SnackbarProvider>
    </ErrorBoundary>
  );
};

export default App;
